/**
 * Allowlist-guarded publisher for the investor-visible company profile (Task 0.2).
 *
 * The public `startup_profiles` row is visible to investors WITHOUT data-room
 * access, so the scoring interview / digest may only ever write a small,
 * non-sensitive subset of fields here — and only on explicit founder approval
 * (the approval UX lives in the Phase 2 digest-review screen; this module is
 * the write seam it calls).
 *
 * The allowlist is a HARD ceiling: any key not in {@link PUBLIC_SAFE_KEYS} is
 * dropped before the update is even built, so sensitive data (revenue, burn,
 * runway, funding target, cap table, ticket size, named customers, any specific
 * metric) can never leak to the public profile even if a caller passes it in.
 *
 * Allowlist rationale — spec §3.3 lists the concepts about/description,
 * tagline/one-liner, what-you-do/category, industry, stage. Mapped to the REAL
 * `startup_profiles` columns (see database.types.ts), the non-sensitive columns
 * that actually exist are:
 *   - `about`    (the narrative — added by migration add_startup_about; the
 *                 digest's headline publish target)
 *   - `tagline`  (the one-liner)
 *   - `industry`
 *   - `stage`
 * There is still NO `description` / `category` column on `startup_profiles`, so
 * those alias keys are intentionally omitted — allowing them would produce a
 * "column does not exist" write error.
 */

import { supabase } from '@/lib/supabase/client';
import { handleError } from '@/lib/errors';
import type { Database } from '@/lib/supabase/database.types';

type StartupProfileUpdate = Database['public']['Tables']['startup_profiles']['Update'];

/**
 * The ONLY fields the interview / digest may publish to the investor-visible
 * `startup_profiles` row. Non-sensitive, real columns only.
 */
export const PUBLIC_SAFE_KEYS: readonly string[] = ['about', 'tagline', 'industry', 'stage'];

/**
 * Write only allowlisted, non-sensitive fields to the founder's real
 * `startup_profiles` row. Any non-allowlisted key is hard-filtered out first.
 * If nothing survives the filter, this is a no-op success (no empty update is
 * issued).
 */
export async function publishPublicSafeFields(
  startupId: string,
  fields: Record<string, unknown>,
): Promise<{ error: unknown }> {
  // Hard allowlist filter — build the payload from the allowlist, never from
  // the caller's keys, so an unknown/sensitive key can never slip through.
  const filtered: Record<string, unknown> = {};
  for (const key of PUBLIC_SAFE_KEYS) {
    if (key in fields && fields[key] !== undefined) {
      filtered[key] = fields[key];
    }
  }

  // Normalize `stage` to the DB enum (pre-seed | seed | series-a | series-b |
  // series-c+). The review screen / profile can carry a display-cased value like
  // "Seed" or "Series A" that the enum rejects (22P02) — and a rejected stage
  // fails the WHOLE publish, silently dropping about/tagline with it (Adi 2026-07-22).
  if (typeof filtered.stage === 'string') {
    const VALID_STAGES = new Set(['pre-seed', 'seed', 'series-a', 'series-b', 'series-c+']);
    const norm = filtered.stage.trim().toLowerCase().replace(/\s+/g, '-');
    if (VALID_STAGES.has(norm)) {
      filtered.stage = norm;
    } else {
      // Unknown/blank stage → drop it rather than fail the publish.
      delete filtered.stage;
    }
  }

  // Nothing safe to write → succeed without touching the DB.
  if (Object.keys(filtered).length === 0) {
    return { error: null };
  }

  try {
    const { error } = await supabase
      .from('startup_profiles')
      .update(filtered as StartupProfileUpdate)
      .eq('id', startupId);

    if (error) {
      handleError(error, { context: 'publishPublicSafeFields' });
    }
    return { error };
  } catch (err) {
    handleError(err, { context: 'publishPublicSafeFields' });
    return { error: err };
  }
}
